import { useParams, Link } from 'react-router-dom';
import { ChevronLeft, FileText, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { useAuditorControlDetail } from '@/api/hooks/useAuditorAccess';

export function AuditorControlDetailPage() {
  const { frameworkId, controlId } = useParams<{ frameworkId: string; controlId: string }>();
  const { data: ctrl, isLoading } = useAuditorControlDetail(frameworkId!, controlId!);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (!ctrl) return <p className="text-text-tertiary">Control not found</p>;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Link to={`/auditor/frameworks/${frameworkId}`} className="text-text-tertiary hover:text-text-primary">
          <ChevronLeft className="h-4 w-4" />
        </Link>
        <div>
          <span className="font-mono text-xs text-text-tertiary">{ctrl.controlId}</span>
          <h1 className="text-xl font-semibold text-text-primary">{ctrl.title}</h1>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Description</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-text-secondary whitespace-pre-wrap">{ctrl.description}</p>
            </CardContent>
          </Card>

          {ctrl.implementationGuidance && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Implementation Guidance</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-text-secondary whitespace-pre-wrap">{ctrl.implementationGuidance}</p>
              </CardContent>
            </Card>
          )}

          {ctrl.implementationNotes && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Implementation Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-text-secondary whitespace-pre-wrap">{ctrl.implementationNotes}</p>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-sm">
                <FileText className="h-4 w-4" />
                Evidence ({ctrl.evidenceCount})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="mb-3 text-xs text-text-tertiary">
                Evidence details shown here are recorded for audit traceability. Source files are retained by the organisation and available from them on request.
              </p>
              {ctrl.evidence.length === 0 ? (
                <p className="text-sm text-text-tertiary">No evidence items</p>
              ) : (
                <ul className="space-y-2">
                  {ctrl.evidence.map((e) => (
                    <li key={e.id} className="rounded-md border border-border-subtle p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-medium text-text-primary text-sm">{e.title}</p>
                          <p className="text-xs text-text-tertiary">
                            {e.evidenceType.replace(/_/g, ' ')} · {e.source}
                            {e.sourceModule ? ` · ${e.sourceModule}` : ''}
                          </p>
                        </div>
                        {e.isExpired && (
                          <Badge variant="rose" className="text-xs shrink-0">Expired</Badge>
                        )}
                      </div>
                      {e.contentSummary && (
                        <p className="mt-1 text-xs text-text-secondary">{e.contentSummary}</p>
                      )}
                      <div className="mt-1 flex items-center gap-3 text-xs text-text-tertiary">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          Collected {new Date(e.collectedAt).toLocaleDateString()}
                        </span>
                        {e.validUntil && (
                          <span>
                            Valid until {new Date(e.validUntil).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <Card>
            <CardContent className="p-4 space-y-3">
              <div>
                <p className="text-xs text-text-tertiary">Status</p>
                <p className="text-sm font-medium text-text-primary">
                  {ctrl.assignmentStatus?.replace(/_/g, ' ') ?? 'Unassigned'}
                </p>
              </div>
              {ctrl.category && (
                <div>
                  <p className="text-xs text-text-tertiary">Category</p>
                  <p className="text-sm text-text-primary">{ctrl.category}</p>
                </div>
              )}
              {ctrl.evidenceTypes.length > 0 && (
                <div>
                  <p className="mb-1 text-xs text-text-tertiary">Expected Evidence Types</p>
                  <div className="flex flex-wrap gap-1">
                    {ctrl.evidenceTypes.map((et) => (
                      <Badge key={et} variant="muted" className="text-xs">
                        {et.replace(/_/g, ' ')}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {ctrl.testProcedures && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Test Procedures</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-text-secondary whitespace-pre-wrap">{ctrl.testProcedures}</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
